import type { QuoteApi } from './types';

export function useQuoteApi(): QuoteApi {
  const quoteStore = useQuoteStore();
  const { ensureAuthenticated } = useDjustAuth();

  const getMasterQuotes = async (
    params: GetMasterQuotesRequest = {},
    useStore: boolean = true
  ): Promise<GetMasterQuotesResponse | null> => {
    try {
      await ensureAuthenticated();

      // Make an API call using useFetch
      const { data, error } = await useFetch<GetMasterQuotesResponse>('/api/quotes', {
        params: {
          fromCustomerAccount: params.fromCustomerAccount,
          page: params.pageable?.page,
          size: params.pageable?.size,
        },
        key: `getquotes-${JSON.stringify(params)}`, // Unique key for caching
      });

      // Check for errors
      if (error.value) {
        console.error('Failed to retrieve quotes:', error.value);
        throw new Error('Failed to retrieve quotes.');
      }

      // Optionally update the store with the fetched quotes
      if (useStore && data.value) {
        quoteStore.setQuotes(data.value.quotes);
      }

      return data.value;
    } catch (e) {
      const error = e as ApiError;
      console.error('Unexpected error while retrieving quote:', error.message || error);
      throw new Error('Failed to retrieve master quote. Please try again later.');
    }
  };

  const createMasterQuote = async (
    params: CreateMasterQuoteRequest
  ): Promise<CreateMasterQuoteResponse | null> => {
    try {
      await ensureAuthenticated();

      // Make an API call using useFetch
      const { data, error } = await useFetch<CreateMasterQuoteResponse>('/api/quotes/create', {
        method: 'POST',
        body: params,
        key: `createmasterquote-${JSON.stringify(params)}`, // Unique key for caching
      });

      // Check for errors
      if (error.value) {
        console.error('Failed to create master quotes:', error.value);
        throw new Error('Failed to create a new master quote.');
      }

      return data.value;
    } catch (e) {
      const error = e as ApiError;
      console.error('Unexpected error while creating master quote:', error);
      throw new Error('Failed to create master quote. Please try again later.');
    }
  };

  const deleteMasterQuote = async (params: DeleteMasterQuoteRequest): Promise<void> => {
    try {
      await ensureAuthenticated();

      // Validate that masterQuoteId is provided
      const { masterQuoteId } = params;
      if (!masterQuoteId) {
        throw new Error('The "masterQuoteId" parameter is required.');
      }
      // Call the API to delete the master quote
      await useFetch(`/api/quotes/${masterQuoteId}/delete`, {
        method: 'DELETE',
        key: `deletemasterquote-${masterQuoteId}`, // Unique key for caching
      });
    } catch (e) {
      const error = e as ApiError;
      console.error('Failed to delete master quote:', error.message || error);
      throw new Error('An unexpected error occurred while deleting the master quote.');
    }
  };

  const getMasterQuoteById = async (
    params: GetMasterQuoteRequest
  ): Promise<GetMasterQuoteResponse | null> => {
    try {
      await ensureAuthenticated();

      // Validate that masterQuoteId is provided
      const { masterQuoteId } = params;
      if (!masterQuoteId) {
        throw new Error('The "masterQuoteId" parameter is required.');
      }

      // Call API to fetch the master quote by ID
      const { data, error } = await useFetch<GetMasterQuoteResponse>(
        `/api/quotes/${masterQuoteId}`,
        {
          key: `getmasterquote-${masterQuoteId}`, // Unique key for caching
        }
      );

      // Handle potential API errors
      if (error.value) {
        console.error('Failed to retrieve master quote:', error.value);
        throw new Error('Failed to retrieve master quote.');
      }

      // Return the master quote response
      return data.value;
    } catch (e) {
      const error = e as ApiError;
      console.error('Failed to retrieve master quote:', error.message || error);
      throw new Error('Failed to retrieve master quote. Please try again later.');
    }
  };

  const updateMasterQuote = async (
    params: UpdateMasterQuoteRequest
  ): Promise<UpdateMasterQuoteResponse | null> => {
    try {
      await ensureAuthenticated();

      // Validate parameters
      const {
        masterQuoteId,
        customFieldValues,
        masterQuoteDetailsToAdd,
        masterQuoteDetailsToRemove,
      } = params;
      if (!masterQuoteId) {
        throw new Error('The "masterQuoteId" parameter is required.');
      }

      // Call the API to update the master quote
      const { data, error } = await useFetch<UpdateMasterQuoteResponse>(
        `/api/quotes/${masterQuoteId}/update`,
        {
          method: 'PUT',
          body: { customFieldValues, masterQuoteDetailsToAdd, masterQuoteDetailsToRemove },
          key: `updatemasterquote-${masterQuoteId}`, // Unique key for caching
        }
      );

      // Handle potential API errors
      if (error.value) {
        console.error('Failed to update master quote:', error.value);
        switch (error.value.statusCode) {
          case 400:
            throw new Error('Missing master quote id.');
          case 404:
            throw new Error('Master quote not found.');
          default:
            throw new Error('Failed to update master quote.');
        }
      }

      return data.value;
    } catch (e) {
      const error = e as ApiError;
      console.error('Failed to update master quote:', error.message || error);
      throw new Error('An unexpected error occurred while updating the master quote.');
    }
  };

  const addOrUpdateQuote = async (
    params: {
      actionType: 'create' | 'update';
      name?: string;
      quoteId?: string;
      currency: string;
      lines: Array<{
        variantId: string;
        quantity: number;
      }>;
    },
    useStore = true
  ): Promise<any> => {
    try {
      if (params.actionType === 'create' && !params.name) {
        throw new Error('Name is required to create a new quote.');
      }
      if (params.actionType === 'update' && !params.quoteId) {
        throw new Error('Quote ID is required to update an existing quote.');
      }

      let quoteIdToUse: string;

      if (params.actionType === 'create') {
        const newQuote = (
          await createMasterQuote({
            name: params.name || '',
          })
        )?.quote;
        if (useStore) {
          quoteStore.quotes.push(newQuote as MasterQuote);
        }
        quoteIdToUse = newQuote?.id || '';
      } else {
        quoteIdToUse = params.quoteId!;
      }

      const quote = quoteStore.quotes.find((q) => q.id === quoteIdToUse) ?? quoteStore.quote;
      const linesToUpdate = params.lines.map((line) => {
        let quantity = line.quantity;
        const variant = quote.masterQuoteDetails.find(
          (quoteDetail: MasterQuoteDetail) => quoteDetail.productVariant.id === line.variantId
        );
        if (variant) {
          quantity += variant.quantity;
        }
        return {
          quantity,
          productVariantId: line.variantId,
        };
      });

      const requestParams = {
        masterQuoteId: quoteIdToUse,
        customFieldValues: [],
        masterQuoteDetailsToAdd: linesToUpdate,
        masterQuoteDetailsToRemove: [],
      };
      const response = await updateMasterQuote(requestParams);
      if (useStore && response) {
        quoteStore.updateQuote(response.quote.id, response.quote);
      }
      return response;
    } catch (error) {
      console.error('Failed to add products to quote:', error);
      throw error;
    }
  };

  const createSupplierQuotes = async (
    params: CreateSupplierQuotesRequest
  ): Promise<CreateSupplierQuotesResponse | null> => {
    try {
      await ensureAuthenticated();

      // Make an API call using useFetch
      const { data, error } = await useFetch<CreateSupplierQuotesResponse>(
        '/api/quotes/supplier-quotes/create',
        {
          method: 'POST',
          body: params,
          key: `createsupplierquotes-${JSON.stringify(params)}`, // Unique key for caching
        }
      );

      // Check for errors
      if (error.value) {
        console.error('Failed to create supplier quotes:', error.value);
        throw new Error('Failed to create a new supplier quotes.');
      }

      return data.value;
    } catch (e) {
      const error = e as ApiError;
      console.error('Unexpected error while creating supplier quotes:', error);
      throw new Error('Failed to create supplier quotes. Please try again later.');
    }
  };

  const getSupplierQuoteById = async (
    params: GetSupplierQuoteRequest
  ): Promise<GetSupplierQuoteResponse | null> => {
    try {
      await ensureAuthenticated();

      // Validate that supplierQuoteId is provided
      const { supplierQuoteId } = params;
      if (!supplierQuoteId) {
        throw new Error('The "supplierQuoteId" parameter is required.');
      }

      // Call API to fetch the supplier quote by ID
      const { data, error } = await useFetch<GetSupplierQuoteResponse>(
        `/api/quotes/supplier-quotes/${supplierQuoteId}`,
        {
          key: `getsupplierquote-${supplierQuoteId}`, // Unique key for caching
        }
      );

      // Handle potential API errors
      if (error.value) {
        console.error('Failed to retrieve supplier quote:', error.value);
        throw new Error('Failed to retrieve supplier quote.');
      }

      // Return the supplier quote response
      return data.value;
    } catch (e) {
      const error = e as ApiError;
      console.error('Failed to retrieve supplier quote:', error.message || error);
      throw new Error('Failed to retrieve supplier quote. Please try again later.');
    }
  };

  const updateSupplierQuoteCustomFields = async (
    params: UpdateSupplierQuoteCustomFieldsRequest
  ): Promise<UpdateSupplierQuoteCustomFieldsResponse | null> => {
    try {
      await ensureAuthenticated();

      // Validate parameters
      const { supplierQuoteId, customFieldValues, idType } = params;
      if (!supplierQuoteId) {
        throw new Error('The "supplierQuoteId" parameter is required.');
      }

      // Call the API to update the supplier quote
      const { data, error } = await useFetch<UpdateSupplierQuoteCustomFieldsResponse>(
        `/api/quotes/supplier-quotes/${supplierQuoteId}/custom-fields`,
        {
          method: 'PATCH',
          body: { customFieldValues, idType },
          key: `updatesupplierquotecustomfields-${supplierQuoteId}`, // Unique key for caching
        }
      );

      // Handle potential API errors
      if (error.value) {
        console.error('Failed to update the custom fields of supplier quote:', error.value);
        switch (error.value.statusCode) {
          case 400:
            throw new Error('Missing supplier quote id.');
          case 404:
            throw new Error('Supplier quote not found.');
          default:
            throw new Error('Failed to update the custom fields of supplier quote.');
        }
      }

      return data.value;
    } catch (e) {
      const error = e as ApiError;
      console.error(
        'Failed to update the custom fields of supplier quote:',
        error.message || error
      );
      throw new Error(
        'An unexpected error occurred while updating the custom fields of the supplier quote.'
      );
    }
  };

  const postMessageToSupplierQuote = async (
    params: PostMessageToSupplierQuoteRequest
  ): Promise<PostMessageToSupplierQuoteResponse | null> => {
    try {
      await ensureAuthenticated();

      const { supplierQuoteId, message, username } = params;

      // Make an API call using useFetch
      const { data, error } = await useFetch<PostMessageToSupplierQuoteResponse>(
        `/api/quotes/supplier-quotes/${supplierQuoteId}/message`,
        {
          method: 'POST',
          body: { message, username },
          key: `postmessagesupplierquote-${JSON.stringify(params)}`, // Unique key for caching
        }
      );

      // Check for errors
      if (error.value) {
        console.error('Failed to post message to supplier quote:', error.value);
        throw new Error('Failed to post message to supplier quote.');
      }

      return data.value;
    } catch (e) {
      const error = e as ApiError;
      console.error('Unexpected error while posting message to supplier quote:', error);
      throw new Error('Failed to post message to supplier quote. Please try again later.');
    }
  };

  const declineSupplierQuote = async (
    params: DeclineSupplierQuoteRequest
  ): Promise<DeclineSupplierQuoteResponse | null> => {
    try {
      await ensureAuthenticated();

      // Validate parameters
      const { supplierQuoteId } = params;
      if (!supplierQuoteId) {
        throw new Error('The "supplierQuoteId" parameter is required.');
      }

      // Call the API to deline the supplier quote
      const { data, error } = await useFetch<DeclineSupplierQuoteResponse>(
        `/api/quotes/supplier-quotes/${supplierQuoteId}/decline`,
        {
          method: 'PUT',
          key: `declinesupplierquote-${supplierQuoteId}`, // Unique key for caching
        }
      );

      // Handle potential API errors
      if (error.value) {
        console.error('Failed to deline supplier quote:', error.value);
        switch (error.value.statusCode) {
          case 400:
            throw new Error('Missing supplier quote id.');
          case 404:
            throw new Error('Supplier quote not found.');
          default:
            throw new Error('Failed to decline supplier quote.');
        }
      }

      return data.value;
    } catch (e) {
      const error = e as ApiError;
      console.error('Failed to decline supplier quote:', error.message || error);
      throw new Error('An unexpected error occurred while declining the supplier quote.');
    }
  };

  const acceptSupplierQuote = async (
    params: AcceptSupplierQuoteRequest
  ): Promise<AcceptSupplierQuoteResponse | null> => {
    try {
      await ensureAuthenticated();

      // Validate parameters
      const { supplierQuoteId } = params;
      if (!supplierQuoteId) {
        throw new Error('The "supplierQuoteId" parameter is required.');
      }

      // Call the API to deline the supplier quote
      const { data, error } = await useFetch<AcceptSupplierQuoteResponse>(
        `/api/quotes/supplier-quotes/${supplierQuoteId}/accept`,
        {
          method: 'PUT',
          key: `acceptsupplierquote-${supplierQuoteId}`, // Unique key for caching
        }
      );

      // Handle potential API errors
      if (error.value) {
        console.error('Failed to deline supplier quote:', error.value);
        switch (error.value.statusCode) {
          case 400:
            throw new Error('Missing supplier quote id.');
          case 404:
            throw new Error('Supplier quote not found.');
          default:
            throw new Error('Failed to accept supplier quote.');
        }
      }

      return data.value;
    } catch (e) {
      const error = e as ApiError;
      console.error('Failed to accept supplier quote:', error.message || error);
      throw new Error('An unexpected error occurred while accepting the supplier quote.');
    }
  };

  const initializeOrderFromSupplierQuote = async (
    params: InitializeOrderFromSupplierQuoteRequest
  ): Promise<InitializeOrderFromSupplierQuoteResponse | null> => {
    try {
      await ensureAuthenticated();

      const { supplierQuoteId, nbPreviewLines, quoteLineIdsAndQuantities } = params;

      // Make an API call using useFetch
      const { data, error } = await useFetch<InitializeOrderFromSupplierQuoteResponse>(
        `/api/quotes/supplier-quotes/${supplierQuoteId}/initialize-order`,
        {
          method: 'POST',
          params: { nbPreviewLines },
          body: { quoteLineIdsAndQuantities },
          key: `initializeordersupplierquote-${JSON.stringify(params)}`, // Unique key for caching
        }
      );

      // Check for errors
      if (error.value) {
        console.error('Failed to initialize order from supplier quote:', error.value);
        throw new Error('Failed to initialize order from supplier quote.');
      }

      return data.value;
    } catch (e) {
      const error = e as ApiError;
      console.error('Unexpected error while posting message to supplier quote:', error);
      throw new Error('Failed to initialize order from supplier quote. Please try again later.');
    }
  };

  return {
    getMasterQuotes,
    createMasterQuote,
    deleteMasterQuote,
    getMasterQuoteById,
    updateMasterQuote,
    createSupplierQuotes,
    getSupplierQuoteById,
    updateSupplierQuoteCustomFields,
    postMessageToSupplierQuote,
    addOrUpdateQuote,
    declineSupplierQuote,
    acceptSupplierQuote,
    initializeOrderFromSupplierQuote,
  };
}
